/**
 * Normalize an AI-emitted preparation method string (e.g. "finely chopped",
 * "Room Temperature", "diced") into a `recipes.prepMethods.*` next-intl key.
 *
 * If the input doesn't map to a known key, the caller should fall back to
 * rendering the raw method string unchanged — prep methods are free-text on
 * `recipe_ingredients.preparation_method` and the AI may emit anything.
 */
export function prepMethodKey(method: string): string | null {
  const normalized = method.trim().toLowerCase();
  if (!normalized) return null;

  // Multi-word entries take precedence over single-word matches.
  const multiWord: Record<string, string> = {
    'finely chopped': 'finelyChopped',
    'roughly chopped': 'roughlyChopped',
    'coarsely chopped': 'roughlyChopped',
    'finely diced': 'finelyDiced',
    'small dice': 'finelyDiced',
    'thinly sliced': 'thinlySliced',
    'thinly slice': 'thinlySliced',
    'thickly sliced': 'thicklySliced',
    'finely grated': 'finelyGrated',
    'room temperature': 'roomTemperature',
    'at room temperature': 'roomTemperature',
    'pat dry': 'patDry',
    'patted dry': 'patDry',
    'rinsed and drained': 'rinsedAndDrained',
    'drained and rinsed': 'drainedAndRinsed',
    'peeled and diced': 'peeledAndDiced',
    'peeled and chopped': 'peeledAndChopped',
    'peeled and grated': 'peeledAndGrated',
    'peeled and sliced': 'peeledAndSliced',
    'halved and seeded': 'halvedAndSeeded',
    'halved and pitted': 'halvedAndPitted',
    'trimmed and halved': 'trimmedAndHalved',
    'freshly cracked': 'freshlyCracked',
    'freshly ground': 'freshlyCracked',
    'extra virgin': 'extraVirgin',
    'seeds removed': 'seedsRemoved',
    'chopped leaves': 'choppedLeaves',
    'leaves chopped': 'choppedLeaves',
    'lightly beaten': 'lightlyBeaten',
    'freshly squeezed': 'freshlySqueezed',
  };
  if (multiWord[normalized]) return multiWord[normalized];

  const singleWord: Record<string, string> = {
    raw: 'raw',
    uncooked: 'uncooked',
    cooked: 'cooked',
    chopped: 'chopped',
    diced: 'diced',
    dice: 'diced',
    minced: 'minced',
    mince: 'minced',
    sliced: 'sliced',
    slice: 'sliced',
    grated: 'grated',
    grate: 'grated',
    ground: 'ground',
    fresh: 'fresh',
    dried: 'dried',
    shredded: 'shredded',
    shred: 'shredded',
    crushed: 'crushed',
    crush: 'crushed',
    smashed: 'smashed',
    smash: 'smashed',
    pressed: 'pressed',
    press: 'pressed',
    peeled: 'peeled',
    peel: 'peeled',
    halved: 'halved',
    halve: 'halved',
    quartered: 'quartered',
    quarter: 'quartered',
    cubed: 'cubed',
    cube: 'cubed',
    julienned: 'julienned',
    julienne: 'julienned',
    whole: 'whole',
    softened: 'softened',
    soft: 'softened',
    melted: 'melted',
    melt: 'melted',
    toasted: 'toasted',
    toast: 'toasted',
    rinsed: 'rinsed',
    rinse: 'rinsed',
    drained: 'drained',
    drain: 'drained',
    trimmed: 'trimmed',
    trim: 'trimmed',
    deveined: 'deveined',
    devein: 'deveined',
    stemmed: 'stemmed',
    stem: 'stemmed',
    seeded: 'seeded',
    seed: 'seeded',
    deseeded: 'seeded',
    torn: 'torn',
    tear: 'torn',
    boneless: 'boneless',
    skinless: 'skinless',
    frozen: 'frozen',
    thawed: 'thawed',
    thaw: 'thawed',
    cold: 'cold',
    warm: 'warm',
    hot: 'hot',
    beaten: 'beaten',
    beat: 'beaten',
    whisked: 'whisked',
    whisk: 'whisked',
    zested: 'zested',
    zest: 'zested',
    juiced: 'juiced',
    juice: 'juiced',
    cored: 'cored',
    core: 'cored',
    pitted: 'pitted',
    pit: 'pitted',
    washed: 'washed',
    wash: 'washed',
    scrubbed: 'scrubbed',
    scrub: 'scrubbed',
    deboned: 'deboned',
    debone: 'deboned',
    soaked: 'soaked',
    soak: 'soaked',
    blanched: 'blanched',
    blanch: 'blanched',
    stoned: 'stoned',
    shelled: 'shelled',
    shell: 'shelled',
    crumbled: 'crumbled',
    crumble: 'crumbled',
    large: 'large',
    medium: 'medium',
    small: 'small',
  };
  if (singleWord[normalized]) return singleWord[normalized];

  return null;
}

/**
 * A segment of a tokenised preparation method string.
 *
 * - `key`: the segment matched a known prep method — caller should translate
 *   via `recipes.prepMethods.<value>`.
 * - `sep`: the segment is a separator the caller should translate (`and`/`or`)
 *   or render verbatim (`comma` → ", ").
 * - `raw`: the segment didn't match any known key — render as-is.
 */
export type PrepSegment =
  | { type: 'key'; value: string }
  | { type: 'sep'; value: 'and' | 'or' | 'comma' }
  | { type: 'raw'; value: string };

/**
 * Tokenise a preparation method string into segments suitable for rendering
 * a translated, localised version.
 *
 * The AI emits compound free-text like "peeled and diced", "drained, rinsed",
 * "crushed or chopped", and occasionally adds qualifier clauses. We:
 * 1. Try an exact whole-string match first — single segment.
 * 2. Otherwise split on " and " / " or " / "," preserving separators,
 *    then try to map each chunk to a key. Unknown chunks become `raw` so
 *    they at least render unchanged instead of being dropped.
 */
export function prepMethodSegments(method: string): PrepSegment[] {
  const trimmed = method.trim();
  if (!trimmed) return [];

  // Exact match — single segment, cheapest case.
  const directKey = prepMethodKey(trimmed);
  if (directKey) return [{ type: 'key', value: directKey }];

  // Split on conjunctions/separators, preserving them as `sep` tokens so the
  // caller can pick locale-aware joiners ("and" → "und" / "or" → "oder").
  const pattern = /\s*(,|\sand\s|\sor\s)\s*/gi;
  const segments: PrepSegment[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(trimmed)) !== null) {
    const chunk = trimmed.substring(lastIndex, match.index).trim();
    if (chunk) {
      const key = prepMethodKey(chunk);
      segments.push(key ? { type: 'key', value: key } : { type: 'raw', value: chunk });
    }
    const sepRaw = match[1].toLowerCase().trim();
    const sep: 'and' | 'or' | 'comma' =
      sepRaw === ',' ? 'comma' : sepRaw === 'or' ? 'or' : 'and';
    segments.push({ type: 'sep', value: sep });
    lastIndex = pattern.lastIndex;
  }
  const tail = trimmed.substring(lastIndex).trim();
  if (tail) {
    const key = prepMethodKey(tail);
    segments.push(key ? { type: 'key', value: key } : { type: 'raw', value: tail });
  }

  // If every non-separator segment is unknown, return a single raw segment so
  // we don't fragment something we can't translate (preserves AI nuance).
  const nonSep = segments.filter((s) => s.type !== 'sep');
  if (nonSep.length === 0) return [{ type: 'raw', value: trimmed }];
  if (nonSep.every((s) => s.type === 'raw')) {
    return [{ type: 'raw', value: trimmed }];
  }

  return segments;
}
